import { assertNever } from '../../lib/exhaustive';
import {
  MAPPING_ACTION_FIELDS,
  MAPPING_ACTION_KEYS,
  MAPPING_ACTION,
  MAPPING_ATYPE,
  MAPPING_SEPARATOR,
  MAPPING_TRIGGER,
  fieldSeparatorFor,
  repeatSeparatorFor,
} from '../../lib/rcu/catalog/mapping';
import type { MappingActionKey } from '../../lib/rcu/catalog/mapping';
import type { MappingEntryReading } from '../../lib/rcu/link/readings';
import type { RcuSceneEntry } from '../../lib/rcu/wire';
import { keySelector } from './catalog/family';
import type { KeypadSelector } from './catalog/family';
import { KEYPAD_ISSUE, blocking } from './catalog/issue';
import type { KeypadIssue } from './catalog/issue';
import { KEY_STATE_WIRE, KEY_STATES } from './catalog/keyState';
import type { KeyState } from './catalog/keyState';
import { valueToWire } from './catalog/value';
import { familyOf, loadsFor } from './model';
import type { Keypad, KeypadKey, LoadState } from './model';
import { CHANNEL_KIND } from '../../lib/rcu/catalog/channel';
import { findTarget, valueFitsTarget } from './targets';
import type { MappingTarget } from './targets';

/**
 * Turning a panel into the entries the firmware stores.
 *
 * One entry per key state that drives something, keyed by the trigger the device
 * announces: `x = [group, address, button, state]`. The feature writes relay
 * actions into `a` and DIN actions into `d`, which is all a room keypad needs;
 * LED acknowledgement, DALI and timed actions are left to the raw mapping editor.
 *
 * The firmware parses those strings positionally and truncates rather than
 * refusing, so nothing invalid is ever assembled here: a state whose output or
 * value does not check out is reported, not written.
 */

/** The four numbers that identify a key state to the device. */
export const triggerOf = (selector: KeypadSelector, state: KeyState): readonly number[] => [
  selector.group,
  selector.address,
  selector.button,
  KEY_STATE_WIRE[state],
];

/**
 * How a trigger is remembered between runs: its own four numbers, so a stored
 * fingerprint and a read-back entry can be matched without keeping a second key
 * format in step with the wire.
 */
export const triggerKey = (selector: KeypadSelector, state: KeyState): string =>
  triggerKeyOfVector(triggerOf(selector, state));

/** The key a read answer arrives under, spelled the same way. */
export const triggerKeyOfVector = (x: readonly number[]): string =>
  x.join(MAPPING_SEPARATOR.FIELDS);

/** One key state, as it will be written. */
export interface EncodedState {
  readonly triggerKey: string;
  readonly selector: KeypadSelector;
  readonly state: KeyState;
  readonly entry: RcuSceneEntry;
  /** Canonical form of `entry`, for telling "the same mapping" from a change. */
  readonly fingerprint: string;
}

export interface EncodedKeypad {
  /** The states that drive something, in key order. */
  readonly entries: readonly EncodedState[];
  /** States that drive nothing: nothing to write and nothing to compare. */
  readonly emptyStates: number;
  /** Everything that would keep a state from being written correctly. */
  readonly issues: readonly KeypadIssue[];
}

/**
 * One canonical spelling of an entry.
 *
 * Fields are emitted in catalog order and missing ones are left out, so an entry
 * the device reports in a different order — or with `d` before `a` — is still
 * recognised as the same mapping.
 */
export const canonicalEntry = (entry: RcuSceneEntry): RcuSceneEntry => {
  const fields: Partial<Record<MappingActionKey, string>> = {};
  for (const key of MAPPING_ACTION_KEYS) {
    const value = entry[key];
    if (value !== undefined) fields[key] = value;
  }
  return {
    atype: MAPPING_ATYPE.SCENE,
    x: [...entry.x],
    ...(entry.k === undefined ? {} : { k: entry.k }),
    ...fields,
  };
};

/** The canonical spelling of an entry, as a string to store and compare. */
export const fingerprintOf = (entry: RcuSceneEntry): string =>
  JSON.stringify(canonicalEntry(entry));

/**
 * Reads an action string back into numbers, e.g. `1:1,2:0` into two pairs, or
 * null when it is not spelled the way the firmware parses that field. The count
 * matters as much as the numbers: the firmware reads positionally, so `a: "1"`
 * is not a shorter action, it is a broken one.
 */
const readActions = (
  key: MappingActionKey,
  raw: string,
): readonly (readonly number[])[] | null => {
  const expected = MAPPING_ACTION_FIELDS[key];
  const parsed: number[][] = [];
  for (const repeat of raw.split(repeatSeparatorFor(key))) {
    const parts = repeat.split(fieldSeparatorFor(key)).map((part) => Number(part.trim()));
    if (parts.length !== expected) return null;
    if (parts.some((part) => !Number.isInteger(part))) return null;
    parsed.push(parts);
  }
  return parsed;
};

/**
 * An entry read back from the device, or null when it is not a scene this
 * feature could have written. `k:0` counts as unreadable: it suppresses the
 * entry's actions, which is not a mapping any key state here means.
 */
export const entryFromReading = (reading: MappingEntryReading): RcuSceneEntry | null => {
  if (reading.atype !== MAPPING_ATYPE.SCENE) return null;
  if (reading.x.length !== MAPPING_TRIGGER.LENGTH) return null;
  if (reading.key !== null) return null;

  const fields: Partial<Record<MappingActionKey, string>> = {};
  for (const action of reading.actions) {
    const key = MAPPING_ACTION_KEYS.find((candidate) => candidate === action.field);
    if (key === undefined || readActions(key, action.raw) === null) return null;
    fields[key] = action.raw;
  }
  return canonicalEntry({ atype: MAPPING_ATYPE.SCENE, x: [...reading.x], ...fields });
};

/** One action field's worth of repeats, or null when there is nothing to write. */
const actionField = (
  key: MappingActionKey,
  repeats: readonly (readonly number[])[],
): Partial<Record<MappingActionKey, string>> => {
  if (repeats.length === 0) return {};
  const spelled = repeats.map((parts) => parts.join(fieldSeparatorFor(key)));
  return { [key]: spelled.join(repeatSeparatorFor(key)) };
};

/** The entry for one key state, or the reason there is none. */
const encodeState = (
  selector: KeypadSelector,
  state: KeyState,
  loads: readonly LoadState[],
  targets: readonly MappingTarget[],
): { readonly entry: RcuSceneEntry } | { readonly issue: KeypadIssue } => {
  const relays: number[][] = [];
  const din: number[][] = [];

  for (const load of loads) {
    const target = findTarget(targets, load.targetId);
    if (target === undefined) {
      return {
        issue: blocking(
          KEYPAD_ISSUE.TARGET_MISSING,
          'One of the outputs in this key state is no longer in the configuration, so this key state was not written.',
        ),
      };
    }
    if (!valueFitsTarget(target, load.value)) {
      return {
        issue: blocking(
          KEYPAD_ISSUE.VALUE_OUT_OF_RANGE,
          `“${target.label}” cannot take the value ${load.value}, so this key state was not written.`,
        ),
      };
    }

    const wire = valueToWire(target.valueKind, load.value);
    switch (target.selector.kind) {
      case CHANNEL_KIND.RCU:
        relays.push([target.selector.relay, wire]);
        break;
      case CHANNEL_KIND.DIN:
        din.push([
          target.selector.module,
          target.selector.address,
          target.selector.channel,
          wire,
        ]);
        break;
      default:
        return assertNever(target.selector);
    }
  }

  return {
    entry: canonicalEntry({
      atype: MAPPING_ATYPE.SCENE,
      x: [...triggerOf(selector, state)],
      ...actionField(MAPPING_ACTION.RELAY, relays),
      ...actionField(MAPPING_ACTION.DIN, din),
    }),
  };
};

/** The trigger a key answers on, or the reason the panel has none yet. */
const selectorOf = (
  keypad: Keypad,
  key: KeypadKey,
  address: number | null,
): { readonly selector: KeypadSelector } | { readonly issue: KeypadIssue } => {
  const selector = keySelector(familyOf(keypad), address, key.index);
  return selector.ok ? { selector: selector.value } : { issue: selector.issue };
};

/**
 * Every trigger a panel's keys answer on at one address, whether or not the state
 * drives anything. Used to forget what a removed panel wrote, and to find what is
 * still stored at an address the panel has left.
 */
export interface TriggerRef {
  readonly triggerKey: string;
  readonly selector: KeypadSelector;
  readonly state: KeyState;
  readonly key: KeypadKey;
}

export const triggersOf = (keypad: Keypad, address?: number | null): readonly TriggerRef[] => {
  const target = address === undefined ? keypad.address : address;
  const refs: TriggerRef[] = [];
  for (const key of keypad.keys) {
    for (const state of KEY_STATES) {
      const resolved = selectorOf(keypad, key, target);
      if (!('selector' in resolved)) continue;
      refs.push({
        triggerKey: triggerKey(resolved.selector, state),
        selector: resolved.selector,
        state,
        key,
      });
    }
  }
  return refs;
};

/** Everything one panel would write, with the issues that stand in the way. */
export const encodeKeypad = (
  keypad: Keypad,
  targets: readonly MappingTarget[],
): EncodedKeypad => {
  const entries: EncodedState[] = [];
  const issues: KeypadIssue[] = [];
  let emptyStates = 0;

  for (const key of keypad.keys) {
    for (const state of KEY_STATES) {
      const loads = loadsFor(key, state);
      if (loads.length === 0) {
        emptyStates += 1;
        continue;
      }

      const resolved = selectorOf(keypad, key, keypad.address);
      if (!('selector' in resolved)) {
        issues.push(resolved.issue);
        continue;
      }

      const encoded = encodeState(resolved.selector, state, loads, targets);
      if ('issue' in encoded) {
        issues.push(encoded.issue);
        continue;
      }
      entries.push({
        triggerKey: triggerKey(resolved.selector, state),
        selector: resolved.selector,
        state,
        entry: encoded.entry,
        fingerprint: fingerprintOf(encoded.entry),
      });
    }
  }

  return { entries, emptyStates, issues };
};
